import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import CookingSessionView from '../components/cooking/CookingSessionView';
import { useBackgroundFix } from '../utils/backgroundFix';
import { getImageUrl } from '../utils/imageUtils';
function RecipeScheduler() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('manual');  const [recipes, setRecipes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
  const [calendarRecipes, setCalendarRecipes] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [forceReparse, setForceReparse] = useState(false);
  const [loadingRecipes, setLoadingRecipes] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schedulerAvailable, setSchedulerAvailable] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [recipeDetails, setRecipeDetails] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  useBackgroundFix('background-fixed', [result, recipes, calendarRecipes, mode]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [recipesRes, healthRes] = await Promise.all([
          axios.get('/api/recipes'),
          axios.get('/api/recipe-scheduler/health'),
        ]);
        setRecipes(recipesRes.data);
        setSchedulerAvailable(healthRes.data.openaiConfigured !== false);
      } catch (err) {
        console.error('Error loading recipe scheduler:', err);
        setError('Failed to load recipes');
      } finally {
        setLoadingRecipes(false);
      }
    };

    fetchInitialData();
  }, []);

  const toggleRecipe = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (mode === 'calendar' && calendarRecipes) {
      setSelectedIds(calendarRecipes.recipes.filter((r) => r.needsCooking).map((r) => r.id));
      return;
    }
    setSelectedIds(recipes.map((r) => r._id));
  };

  const clearAll = () => setSelectedIds([]);

  const closeRecipeModal = () => {
    setSelectedRecipeId(null);
    setRecipeDetails(null);
  };

  const handleRecipeTitleClick = async (recipeId) => {
    if (selectedRecipeId === recipeId && recipeDetails) {
      closeRecipeModal();
      return;
    }

    setSelectedRecipeId(recipeId);
    setLoadingRecipe(true);
    setRecipeDetails(null);

    try {
      const response = await axios.get(`/api/recipes/${recipeId}`);
      setRecipeDetails(response.data);
    } catch (err) {
      console.error('Error fetching recipe details:', err);
      setError('Failed to load recipe details');
      closeRecipeModal();
    } finally {
      setLoadingRecipe(false);
    }
  };

  const getCalendarMealsForRecipe = (recipeId) => {
    if (!calendarRecipes?.recipes) {
      return [];
    }
    const entry = calendarRecipes.recipes.find((r) => r.id === recipeId);
    return entry?.meals || [];
  };
  const handleLoadFromCalendar = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setLoadingCalendar(true);
    setError(null);
    setCalendarRecipes(null);
    setResult(null);

    try {
      const response = await axios.get('/api/recipe-scheduler/calendar-recipes', {
        params: { startDate, endDate },
      });
      setCalendarRecipes(response.data);
      setSelectedIds(
        response.data.recipes.filter((r) => r.needsCooking).map((r) => r.id)
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load calendar recipes');
      setSelectedIds([]);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleBuildTimeline = async () => {
    if (mode === 'calendar' && (!startDate || !endDate)) {
      setError('Please select both start and end dates');
      return;
    }

    if (selectedIds.length === 0) {
      setError('Select at least one recipe.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        recipeIds: selectedIds,
        forceReparse,
      };

      if (mode === 'calendar') {
        payload.startDate = startDate;
        payload.endDate = endDate;
      }

      const response = await axios.post('/api/recipe-scheduler/schedule', payload);
      setResult(response.data);
    } catch (err) {
      const message = err.response?.data?.error
        || (err.message === 'Network Error'
          ? 'Lost connection to the server while building the timeline. This can happen if the dev server restarted mid-request — try again. For many recipes, leave “Re-parse recipes” unchecked so cached steps are reused.'
          : err.message)
        || 'Failed to build timeline';
      console.error('Build timeline error:', err.response?.data || err.message);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const displayRecipes =
    mode === 'calendar' && calendarRecipes
      ? calendarRecipes.recipes.map((r) => ({
          _id: r.id,
          title: r.title,
          totalTime: r.totalTime,
          mealCount: r.mealCount,
          cookMealCount: r.cookMealCount,
          leftoverMealCount: r.leftoverMealCount,
          needsCooking: r.needsCooking,
          meals: r.meals || [],
        }))
      : recipes;

  const canBuildTimeline =
    mode === 'calendar'
      ? calendarRecipes && calendarRecipes.recipes.length > 0 && selectedIds.length > 0
      : selectedIds.length > 0;

  return (
    <>
      <div
        id="background-fixed"
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/edit-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
        }}
      />
      <style>{`
        @media (max-width: 767px) {
          #background-fixed {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100vw;
            min-height: 100vh;
            background-attachment: fixed;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          }
        }
      `}</style>
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-6xl mx-auto">
          <div className="mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Cooking Timeline</h1>
            <p className="text-gray-700 mt-2">
              Pick recipes manually or use your calendar to build an optimized cooking timeline.
            </p>
          </div>

          {schedulerAvailable === false && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4 mb-6">
              OpenAI API key is not configured. Add{' '}
              <code className="bg-amber-100 px-1 rounded">OPENAI_API_KEY</code>{' '}
              to <code className="bg-amber-100 px-1 rounded">backend/.env</code>.
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6" role="alert">
              {error}
            </div>
          )}

          {!result && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Source</h2>

            <div className="flex flex-wrap gap-3 mb-6">
              <button
                type="button"
                onClick={() => {
                  setMode('manual');
                  setCalendarRecipes(null);
                  setSelectedIds([]);
                  setError(null);
                }}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  mode === 'manual'
                    ? 'bg-amber-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Pick recipes
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('calendar');
                  setSelectedIds([]);
                  setError(null);
                }}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  mode === 'calendar'
                    ? 'bg-amber-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                From calendar
              </button>
            </div>

            {mode === 'calendar' && (
              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Choose a date range and load the recipes planned on your calendar.
                  Leftover meals are unchecked automatically — only fresh cooks go on the timeline.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Start date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">End date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleLoadFromCalendar}
                  disabled={loadingCalendar}
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
                >
                  {loadingCalendar ? 'Loading...' : 'Load from calendar'}
                </button>
                {calendarRecipes && (
                  <p className="text-sm text-gray-600 mt-3">
                    Found {calendarRecipes.recipes.length} unique recipe
                    {calendarRecipes.recipes.length === 1 ? '' : 's'} from{' '}
                    {calendarRecipes.totalMeals} meal
                    {calendarRecipes.totalMeals === 1 ? '' : 's'}
                    {calendarRecipes.leftoverMeals > 0 && (
                      <>
                        {' '}
                        ({calendarRecipes.cookMeals} to cook, {calendarRecipes.leftoverMeals}{' '}
                        leftover{calendarRecipes.leftoverMeals === 1 ? '' : 's'})
                      </>
                    )}{' '}
                    between {moment(calendarRecipes.startDate).format('MMM D, YYYY')} and{' '}
                    {moment(calendarRecipes.endDate).format('MMM D, YYYY')}.
                  </p>
                )}
              </div>
            )}

            {mode === 'manual' && (
              <p className="text-gray-600 mb-4">Select one or more dishes to cook together.</p>
            )}

            {loadingRecipes ? (
              <p className="text-gray-600">Loading recipes...</p>
            ) : mode === 'manual' && recipes.length === 0 ? (
              <p className="text-gray-600">No recipes yet. Add some from the All Recipes page.</p>
            ) : mode === 'calendar' && !calendarRecipes ? (
              <p className="text-gray-600">Load recipes from your calendar to preview them here.</p>
            ) : mode === 'calendar' && calendarRecipes?.recipes.length === 0 ? (
              <p className="text-gray-600">No meals found on the calendar for this date range.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {displayRecipes.map((recipe) => (
                  <div
                    key={recipe._id}
                    className={`flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-amber-50 ${
                      mode === 'calendar' && recipe.needsCooking === false ? 'opacity-60' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(recipe._id)}
                      onChange={() => toggleRecipe(recipe._id)}
                      disabled={mode === 'calendar' && recipe.needsCooking === false}
                      className="w-4 h-4 flex-shrink-0"
                      aria-label={`Select ${recipe.title}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRecipeTitleClick(recipe._id)}
                      className="font-medium text-left text-amber-800 hover:text-amber-600 hover:underline"
                      title="View recipe"
                    >
                      {recipe.title}
                    </button>
                    {mode === 'calendar' && recipe.needsCooking === false && (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        Leftovers only
                      </span>
                    )}
                    {mode === 'calendar' && recipe.needsCooking && recipe.leftoverMealCount > 0 && (
                      <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">
                        {recipe.cookMealCount} cook · {recipe.leftoverMealCount} leftover
                      </span>
                    )}
                    {mode === 'calendar' && recipe.mealCount > 1 && recipe.leftoverMealCount === 0 && (
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                        {recipe.mealCount} meals
                      </span>
                    )}
                    {recipe.totalTime && (
                      <span className="text-sm text-gray-500 ml-auto">{recipe.totalTime} min</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(mode === 'manual' || (mode === 'calendar' && calendarRecipes?.recipes.length > 0)) && (
              <div className="flex flex-wrap gap-3 items-center mb-4">
                <button
                  type="button"
                  onClick={selectAll}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {mode === 'calendar' ? 'Select cook meals' : 'Select all'}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
                {mode === 'calendar' && (
                  <span className="text-sm text-gray-600">
                    {selectedIds.length} of {calendarRecipes.recipes.length} selected
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-3 items-center">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={forceReparse}
                  onChange={(e) => setForceReparse(e.target.checked)}
                  className="w-4 h-4"
                />
                Re-parse with AI (ignore cache)
              </label>
              <button
                onClick={handleBuildTimeline}
                disabled={loading || loadingRecipes || loadingCalendar || !canBuildTimeline}
                className="ml-auto px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
              >
                {loading ? 'Building timeline...' : 'Build timeline'}
              </button>
            </div>
          </div>
          )}

          {result && (
            <CookingSessionView
              scheduleResult={result}
              onBack={() => setResult(null)}
            />
          )}
        </div>
      </div>

      {selectedRecipeId && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeRecipeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {loadingRecipe ? (
              <div className="p-8 text-center">
                <p className="text-gray-600">Loading recipe...</p>
              </div>
            ) : recipeDetails ? (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">{recipeDetails.title}</h2>
                  <button
                    type="button"
                    onClick={closeRecipeModal}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                {recipeDetails.imageUrl && (
                  <img
                    src={getImageUrl(recipeDetails.imageUrl)}
                    alt={recipeDetails.title}
                    className="w-full h-48 object-cover rounded-lg mb-4"
                  />
                )}

                {getCalendarMealsForRecipe(selectedRecipeId).length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-semibold text-amber-900 mb-2">Planned on calendar:</p>
                    <ul className="space-y-1 text-sm text-amber-800">
                      {getCalendarMealsForRecipe(selectedRecipeId).map((meal, idx) => (
                        <li key={idx}>
                          <span className="capitalize font-medium">{meal.mealType}</span>
                          {' — '}
                          {moment(meal.date).format('MMM D, YYYY')}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 mb-6 pb-4 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-500">
                      {recipeDetails.totalTime || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">Duration (min)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-500">
                      {recipeDetails.servings}
                    </p>
                    <p className="text-sm text-gray-600">Servings</p>
                  </div>
                </div>

                {recipeDetails.description && (
                  <p className="text-gray-600 mb-6">{recipeDetails.description}</p>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-3">Ingredients</h3>
                  <ul className="space-y-2">
                    {recipeDetails.ingredients?.map((ingredient, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="text-orange-500">•</span>
                        <span className="font-semibold">
                          {ingredient.quantity} {ingredient.unit}
                        </span>
                        <span>{ingredient.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {recipeDetails.instructions?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-3">Instructions</h3>
                    <ol className="space-y-3">
                      {recipeDetails.instructions.map((step, idx) => (
                        <li key={idx} className="flex gap-3">
                          <span className="flex-shrink-0 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {idx + 1}
                          </span>
                          <span className="flex-1">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/recipes/${selectedRecipeId}`)}
                    className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md"
                  >
                    View full recipe
                  </button>
                  <button
                    type="button"
                    onClick={closeRecipeModal}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors shadow-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>  );
}

export default RecipeScheduler;
